import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { CustomDialog } from 'Components/CustomDialog';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { getFileMimeType } from 'Utils/getFileMimeType';
import { downloadFile } from 'Utils/downloadFile';
import { useLoader } from 'Hooks/useLoader';
import { LeftArrowWhite, Download2White, GreenCheckMark } from 'Images';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface IFileViewerDialog {
  showDialog: boolean;
  onClose: () => void;
  base64: string;
  fileExtension: string;
  fileName?: string;
}

const DOWNLOAD_SUCCESS_DISPLAY_MS = 5000;

const isPdfExtension = (ext: string) => ext.toLowerCase() === 'pdf';

const isImageExtension = (ext: string) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(ext.toLowerCase());
};

export const FileViewerDialog = ({
  showDialog,
  onClose,
  base64,
  fileExtension,
  fileName = 'document',
}: IFileViewerDialog) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'success'>(
    'idle'
  );
  const { loader, setIsLoading, isLoading } = useLoader();
  const {
    loader: downloadLoader,
    setIsLoading: setIsDownloading,
    isLoading: isDownloading,
  } = useLoader('20', { width: '100px' }, 20, false);

  const mimeType = getFileMimeType(fileExtension);
  const isPdf = isPdfExtension(fileExtension);
  const isImage = isImageExtension(fileExtension);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  const onDocumentLoadError = () => {
    setIsLoading(false);
  };

  const handlePrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const handleDialogClose = () => {
    setPageNumber(1);
    setNumPages(0);
    setDownloadStatus('idle');
    onClose();
  };

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      downloadFile(base64, fileExtension, fileName);
      setIsDownloading(false);
      setDownloadStatus('success');
      setTimeout(() => setDownloadStatus('idle'), DOWNLOAD_SUCCESS_DISPLAY_MS);
    } catch {
      setIsDownloading(false);
    }
  };

  return (
    <CustomDialog
      showDialog={showDialog}
      onclose={handleDialogClose}
      width='90%'
      height='90vh'
      paperSx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      forceHighZIndex
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: '75px',
          pb: 2,
          px: 2,
        }}
      >
        {isPdf && (
          <>
            {isLoading && loader}
            <Document
              file={`data:application/pdf;base64,${base64}`}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=''
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer
                renderAnnotationLayer
                width={window.innerWidth * 0.9 * 0.85}
              />
            </Document>
            {numPages > 1 && (
              <Stack
                direction='row'
                alignItems='center'
                spacing={2}
                sx={{ mt: 2, pb: 1, justifyContent: 'center' }}
              >
                <IconButton
                  onClick={handlePrevPage}
                  disabled={pageNumber <= 1}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' },
                    width: '30px',
                    height: '30px',
                  }}
                >
                  <Avatar
                    src={LeftArrowWhite}
                    alt=''
                    sx={{
                      width: '16px',
                      height: '16px',
                      transform: 'rotate(180deg)',
                    }}
                  />
                </IconButton>
                <Typography>
                  {pageNumber} / {numPages}
                </Typography>
                <IconButton
                  onClick={handleNextPage}
                  disabled={pageNumber >= numPages}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' },
                    width: '30px',
                    height: '30px',
                  }}
                >
                  <Avatar
                    src={LeftArrowWhite}
                    alt=''
                    sx={{
                      width: '16px',
                      height: '16px',
                    }}
                  />
                </IconButton>
              </Stack>
            )}
          </>
        )}

        {isImage && (
          <Box
            component='img'
            src={`data:${mimeType};base64,${base64}`}
            alt={Dictionary[DICTIONARY.General].watch}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        )}

        {!isPdf && !isImage && (
          <Typography color='error'>
            {Dictionary[DICTIONARY.General].invalidFileError}
          </Typography>
        )}

        {(isPdf || isImage) && (
          <Box sx={{ mt: 2, minHeight: '44px' }}>
            {isDownloading ? (
              downloadLoader
            ) : downloadStatus === 'success' ? (
              <Stack direction='row' alignItems='center' gap={1}>
                <Avatar
                  src={GreenCheckMark}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
                <Typography variant='body6' color='success.main'>
                  {Dictionary[DICTIONARY.Documents].downloadSuccess}
                </Typography>
              </Stack>
            ) : (
              <Button
                onClick={handleDownload}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '22px',
                  px: 3,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Avatar
                  src={Download2White}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
                <Typography variant='body6' color='white'>
                  {Dictionary[DICTIONARY.Documents].download}
                </Typography>
              </Button>
            )}
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};
